import { Injectable } from '@angular/core';

/**
 * In-memory holder for the API access token.
 *
 * SECURITY (audit obs #5 - CWE-613): the access token used to live in
 * localStorage, where it survived tab closes, was readable by any script on the
 * origin, and gave an XSS payload a credential it could exfiltrate and replay
 * long after the user walked away. It now lives in this field and nowhere else.
 *
 * Consequences of holding it in memory only, all handled by HttpConfigInterceptor:
 *  - A page reload starts with nothing to send, so the first call comes back 406
 *    ("No token"). That is recoverable, not a dead session: the httpOnly session
 *    cookie survived the reload, so auth2/admin/session-token mints a fresh token.
 *  - Refresh uses session-token rather than refresh-token precisely because
 *    refresh-token requires the client to POST its previous token, which an
 *    in-memory client cannot produce after a reload. That requirement was the
 *    only reason the token ever had to be written to disk.
 *
 * Must stay `providedIn: 'root'`. The interceptor and the components that read
 * the token have to share one instance - a second instance would hold an empty
 * token and silently log the user out.
 */
@Injectable({ providedIn: 'root' })
export class TokenStoreService {
  private token: string = null;

  /** Current access token, or null when none is held. */
  get(): string {
    return this.token;
  }

  /** Replace the held token. Ignores empty/undefined values. */
  set(token: string): void {
    if (!token || token === 'undefined' || token === 'null') {
      return;
    }
    this.token = token;
  }

  /** Drop the held token. Called on logout and on genuine session failure. */
  clear(): void {
    this.token = null;
  }

  /** True when no token is held - e.g. immediately after a page reload. */
  isEmpty(): boolean {
    return !this.token;
  }
}
